import { Component, OnInit, OnDestroy, Inject, AfterViewInit } from '@angular/core';
import { ModalController, PopoverController } from '@ionic/angular';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { DeviceDetectorService, DeviceInfo } from 'ngx-device-detector';
import { ActiveToast, ToastrService } from 'ngx-toastr';

import { WindowToken } from '../../services/window/window';
import { NameService } from '../../services/name/name.service';
import { EquipmentService } from '../../services/equipment/equipment.service';
import { DolbyService } from '../../services/dolby/dolby.service';
import { UserService } from '../../services/user/user.service';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { SupportCenterService } from '../../services/support-center/support-center.service';
import { WalletService } from '../../services/wallet/wallet.service';
import { defaultPopoverOptions, ThemeService } from '../../services/theme/theme.service';
import { AmplionService } from '../../services/amplion/amplion.service';
import { MuteService } from '../../services/mute/mute.service';
import { UpdatePaymentInfoPage } from '../../modals/update-payment-info/update-payment-info.page';
import { GeneralToastComponent } from '../../toasts/general-toast/general-toast.component';
import { EchoCancellationInfoMenuComponent } from '../../menus/echo-cancellation-info-menu/echo-cancellation-info-menu.component';
import { IPadService } from '../../services/ipad/ipad.service';
import { ConfTypes, FeatureNames, WebhookEvent, WebhookEventNames } from '@sc/types';
import { Locations, SessionTestResults } from '@sc/types';
import { SessionsService } from '../../services/sessions/sessions.service';
import { SettingsService } from '../../services/settings/settings.service';
import { ColorsService } from '../../services/colors/colors.service';
import { RoleLimits, Roles } from '@sc/types';
import { CalifoneService } from '../../services/califone/califone.service';
import { Router } from '@angular/router';
import { ToursService } from '../../services/tours/tours.service';
import * as Rollbar from 'rollbar';
import { RollbarService } from '../../services/rollbar/rollbar.service';
import { RolesService } from '../../services/roles/roles.service';
import { RecordingService } from '../../services/recording/recording.service';
import { TrialService } from '../../services/trial/trial.service';
import { ShowsService } from '../../services/shows/shows.service';
import { environment } from '../../../environments/environment';
import { DailyService } from '../../services/daily/daily.service';
import { DailyCall, DailyRoomInfo } from '@daily-co/daily-js';
import { FeatureFlagService } from '../../services/feature-flags/feature-flags.service';

@Component({
  selector: 'sc-green-room',
  templateUrl: 'green-room.component.html',
  styleUrls: ['green-room.component.scss'],
})
export class GreenRoomComponent implements OnInit, OnDestroy, AfterViewInit {
  user$ = this.userService.activeUser$;
  session$ = this.sessionsService.studioSession$;
  studioOrg$ = this.sessionsService.studioOrg$;
  participant$ = this.sessionsService.participant$;
  theme$ = this.themeService.theme$;
  plan$ = this.walletService.studioPlan$;
  recording$ = this.recordingService.recording$;
  role$ = this.rolesService.role$;
  shows$ = this.showsService.availableShows$;
  featureFlags$ = this.featureFlagsService.featureFlags$;

  echoCancellation: boolean;
  sessionEchoCancellation: boolean;
  joining = false;
  notSupported = false;
  name: string;
  pronouns: string;
  isBelowOneHour: boolean;
  available: string;
  timeWalletToast: ActiveToast<any>;
  muteOnJoin: boolean;
  dolbyInitialized: boolean;
  joinTestSuccess: boolean;
  easterEgg = false;
  deviceInfo: DeviceInfo;
  activeParticipants: Array<string> = [];
  roleLimits = RoleLimits;
  roles = Roles;
  joinButtonTimeout: NodeJS.Timer;
  dailyURL = environment.daily.url;
  dailyInitialized = false;
  previewShutdown = new Subject<void>();

  subs: Array<Subscription> = [];

  constructor(
    private toastrService: ToastrService,
    private router: Router,
    private modalController: ModalController,
    private deviceDetectorService: DeviceDetectorService,
    private userService: UserService,
    private nameService: NameService,
    private equipmentService: EquipmentService,
    private dolbyService: DolbyService,
    private analyticsService: AnalyticsService,
    private supportCenterService: SupportCenterService,
    private walletService: WalletService,
    private themeService: ThemeService,
    private amplionService: AmplionService,
    private muteService: MuteService,
    private iPadService: IPadService,
    private popoverController: PopoverController,
    private califoneService: CalifoneService,
    private sessionsService: SessionsService,
    private recordingService: RecordingService,
    private rolesService: RolesService,
    private settingsService: SettingsService,
    private showsService: ShowsService,
    private colorsService: ColorsService,
    private toursService: ToursService,
    private featureFlagsService: FeatureFlagService,
    private trialService: TrialService,
    private dailyService: DailyService,
    @Inject(RollbarService) private rollbar: Rollbar,
    @Inject(WindowToken) private window: Window
  ) {}

  ngOnInit() {
    this.setupConfType();
    this.setupUser();
    this.setupCommunityEventCheck();
    this.setupPlatformCheck();
    this.setupActiveParticipants();
    this.setupWallet();
    this.setupSettings();
    this.setupEchoCancellation();
    this.setupBalanceCheck();
    if (this.session$?.value?.confType !== ConfTypes.DAILY) {
      this.joinButtonTimeout = setTimeout(() => {
        if (this.session$?.value?.confType !== ConfTypes.DAILY && this.dolbyInitialized === undefined) {
          this.rollbar.error('Timed out waiting for initialize session results');
          this.toastrService.error(
            `This may be due to a network issue.  Check your connection, refresh the page, and try again.`,
            `Timed out while waiting for connection to Dolby`,
            {
              enableHtml: true,
              progressBar: false,
              closeButton: true,
              tapToDismiss: false,
              toastComponent: GeneralToastComponent,
              disableTimeOut: true,
            }
          );
        }
      }, 15000);
    }
    this.dailyService.initRoom().then(() => {
      this.dailyInitialized = true;
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.toursService.startTours();
    }, 0);
  }

  async setupBalanceCheck() {
    await this.studioOrg$.nextExistingValue();
    this.amplionService.balanceLedger(this.studioOrg$.value.orgID);
  }

  testResults(results: SessionTestResults) {
    if (this.session$?.value?.confType === 'daily') return;
    this.dolbyInitialized = results.dolbyInitialized;
    this.joinTestSuccess = results.joinSuccess;
    clearTimeout(this.joinButtonTimeout);
  }

  // async openMoveSessionModal() {
  //   this.analyticsService.track('opened move session modal', { sessionID: this.session$.value.sessionID });
  //   const oldShowID = this.session$.value.showID;
  //   const modal = await this.popoverController.create({
  //     component: MoveSessionPage,
  //     componentProps: { session: this.session$.value },
  //     cssClass: 'move-session-modal',
  //   });
  //   await modal.present();
  //   const resp = await modal.onDidDismiss();
  //   window.history.replaceState(null, '', window.location.href.replace(oldShowID, resp.data));
  // }

  setupEchoCancellation() {
    const sub = this.equipmentService.echoCancellation$.subscribe((echoCancellation) => {
      this.echoCancellation = echoCancellation;
    });

    this.subs.push(sub);
  }

  async openEchoCancellationInfoMenu(click: MouseEvent) {
    const menu = await this.popoverController.create({
      ...defaultPopoverOptions,
      component: EchoCancellationInfoMenuComponent,
      cssClass: 'info-menu',
      event: click,
    });

    await menu.present();
    await this.analyticsService.track('opened echo cancellation info');
  }

  toggleEchoCancellation(event: ICustomEvent) {
    this.echoCancellation = event.detail.checked;
    this.equipmentService.setEchoCancellation(this.echoCancellation);
    this.analyticsService.track('toggled echo cancellation', { enabled: this.echoCancellation });
  }

  async setupCommunityEventCheck() {
    // Check if the session is a community session.
    // if it is, then check if the user is a member of the community. aka has an @SquadCast.fm account.
    // if they're not redirect them to the backstage immediately.
    if (this.session$.value?.communityEvent === true) {
      if (!this.user$.value?.email?.includes('@squadcast.fm') || this.user$.value.guest) {
        this.toastrService.info(
          'You are being Redirected to the Backstage to View and Join the event.',
          '🎙 You are joining a SquadCast Community Event!',
          {
            progressBar: false,
            closeButton: true,
            tapToDismiss: false,
            disableTimeOut: true,
            toastComponent: GeneralToastComponent,
          }
        );
        setTimeout(() => {
          this.router.navigate(
            [`/studio/${this.session$.value.showID}/session/${this.session$.value.sessionID}/location/backstage`],
            { replaceUrl: true }
          );
        }, 1000);
      }
    }
  }

  async setupSettings() {
    await this.muteService.mute$.toPromise();
    const sub = this.settingsService.userAppSettings$.subscribe((settings) => {
      this.muteOnJoin = settings.muteOnJoin;
      if (settings.muteOnJoin && this.muteService.mute$.value) {
        this.muteService.setMute(this.session$.value.sessionID, this.user$.value.uid, false);
      }
    });

    this.subs.push(sub);
  }

  async joinSession() {
    try {
      if (this.session$?.value?.confType === 'daily') {
        await this.joinDailySession();
      } else {
        await this.joinDolbySession();
      }
    } catch (error) {
      console.error(error);
    }
  }

  async joinDailySession(retry = false) {
    const locals = {
      convoOptions: null,
      convoID: null,
    };
    try {
      this.analyticsService.track('clicked join session');
      this.joining = true;
      this.name = await this.nameService.getRandomNameIfEmpty(this.name);
      this.setName();

      // wait for the preview-test streams to be shut down
      if (!retry) {
        await firstValueFrom(this.previewShutdown);
      }

      // build the call url
      const callURL = `${this.dailyURL}/session_${this.session$.value.sessionID}`;
      // join the call
      const call: DailyCall = await this.dailyService.joinCall(callURL);
      if (call) {
        const room: DailyRoomInfo = (await call.room()) as DailyRoomInfo;
        locals.convoID = room.id;
        locals.convoOptions = room;
      }
      this.dailyService.dailyCall$.value.setUserName(this.name);
      this.sessionsService.addParticipant(this.user$.value.uid, this.session$.value.sessionID);
      await this.colorsService.setParticipantColor(this.user$.value.uid, this.colorsService.getRandomAccentColor());
      if (this.session$.value.descriptInstant) {
        // This is so that descript created sessions can not have their links broken after joining the session.
        await this.sessionsService.setDescriptInstant(this.session$.value.sessionID, false);
      }
      let title;
      let message;
      if (this.deviceDetectorService.isMobile() || this.deviceDetectorService.browser === 'Firefox') {
        title = `Keep the studio in focus`;
        message = `Switching to other apps or tabs may stop your recording or prevent it from starting!`;
      } else {
        title = `Recordings will be higher quality than the session.`;
        message = `The quality of the session while recording may fluctuate with network connectivity and selected recording fidelity, but this will not impact the quality of the recordings.`;
      }

      if (this.settingsService.userAppSettings$.value?.showJoinSessionToast ?? true) {
        const joinToast: ActiveToast<GeneralToastComponent> = this.toastrService.info(message, title, {
          progressBar: true,
          progressAnimation: 'decreasing',
          closeButton: true,
          tapToDismiss: false,
          timeOut: 15 * 1000,
          toastComponent: GeneralToastComponent,
        });

        if (!this.user$.value?.guest) {
          joinToast.toastRef.componentInstance.buttons = [
            {
              label: "Don't show again",
              handler: async () => {
                await this.analyticsService.track('clicked dont show again on join toast');
                this.settingsService.setUserAppSettings({ showJoinSessionToast: false });
                joinToast.toastRef.close();
              },
            },
          ];
        }

        if (!this.deviceDetectorService.isMobile()) {
          joinToast.toastRef.componentInstance.learnMoreTopic = 'confQualityDifference';
        }
      }

      const event: WebhookEvent = {
        name: WebhookEventNames.PARTICIPANT_JOIN,
        userName: this.name,
        orgID: this.studioOrg$.value.orgID,
        sessionID: this.session$.value.sessionID,
        sessionTitle: this.session$.value.sessionTitle,
        showID: this.session$.value.showID,
        showName: this.session$.value.showTitle,
        date: this.session$.value.date,
        location: Locations.STAGE,
      };
      await this.califoneService.emitWebhookEvent(event, this.studioOrg$.value.orgID);
      await this.analyticsService.track('joined recording session on the stage', { success: true });
      this.sessionsService.clearParticipantNotifications(this.session$.value.sessionID, this.user$.value.uid);
    } catch (error) {
      if (retry) {
        this.rollbar.error('Failed to Join Daily Session', error, locals);
        this.toastrService.error(
          `This may be due to a network issue.  Check your connection, refresh the page, and try again.<br/><br/><code>${error.message}</code>`,
          `Failed to join the conversation`,
          {
            enableHtml: true,
            progressBar: false,
            closeButton: true,
            tapToDismiss: false,
            toastComponent: GeneralToastComponent,
            disableTimeOut: true,
          }
        );
        await this.analyticsService.track('joined recording session on the stage', { success: false });
      } else {
        await this.dailyService.initRoom();
        await this.joinDailySession(true);
      }
    } finally {
      this.joining = false;
    }
  }

  async joinDolbySession() {
    const locals = {
      convoOptions: null,
      convoID: null,
    };
    try {
      this.analyticsService.track('clicked join session');
      this.joining = true;

      this.name = await this.nameService.getRandomNameIfEmpty(this.name);
      this.setName();

      if (!this.dolbyService.sessionIsOpen()) {
        await this.dolbyService.initSession();
      }

      // if (this.session$.value.videoEnabled === undefined) {
      //   this.session$.value.videoEnabled = this.plan$.value.videoRecording;
      // }

      if (!(await this.checkAvailability())) return;
      const info = await this.dolbyService.joinConversation();
      if (info) locals.convoID = info.id;
      this.sessionsService.addParticipant(this.user$.value.uid, this.session$.value.sessionID);
      await this.colorsService.setParticipantColor(this.user$.value.uid, this.colorsService.getRandomAccentColor());
      if (this.session$.value.descriptInstant) {
        // This is so that descript created sessions can not have their links broken after joining the session.
        await this.sessionsService.setDescriptInstant(this.session$.value.sessionID, false);
      }
      let title;
      let message;
      if (this.deviceDetectorService.isMobile() || this.deviceDetectorService.browser === 'Firefox') {
        title = `Keep the studio in focus`;
        message = `Switching to other apps or tabs may stop your recording or prevent it from starting!`;
      } else {
        title = `Recordings will be higher quality than the session.`;
        message = `The quality of the session while recording may fluctuate with network connectivity and selected recording fidelity, but this will not impact the quality of the recordings.`;
      }

      if (this.settingsService.userAppSettings$.value?.showJoinSessionToast ?? true) {
        const joinToast: ActiveToast<GeneralToastComponent> = this.toastrService.info(message, title, {
          progressBar: true,
          progressAnimation: 'decreasing',
          closeButton: true,
          tapToDismiss: false,
          timeOut: 15 * 1000,
          toastComponent: GeneralToastComponent,
        });

        if (!this.user$.value?.guest) {
          joinToast.toastRef.componentInstance.buttons = [
            {
              label: "Don't show again",
              handler: async () => {
                await this.analyticsService.track('clicked dont show again on join toast');
                this.settingsService.setUserAppSettings({ showJoinSessionToast: false });
                joinToast.toastRef.close();
              },
            },
          ];
        }

        if (!this.deviceDetectorService.isMobile()) {
          joinToast.toastRef.componentInstance.learnMoreTopic = 'confQualityDifference';
        }
      }

      const event: WebhookEvent = {
        name: WebhookEventNames.PARTICIPANT_JOIN,
        userName: this.name,
        orgID: this.studioOrg$.value.orgID,
        sessionID: this.session$.value.sessionID,
        sessionTitle: this.session$.value.sessionTitle,
        showID: this.session$.value.showID,
        showName: this.session$.value.showTitle,
        date: this.session$.value.date,
        location: Locations.STAGE,
      };
      await this.califoneService.emitWebhookEvent(event, this.studioOrg$.value.orgID);
      await this.analyticsService.track('joined recording session on the stage', { success: true });
      this.sessionsService.clearParticipantNotifications(this.session$.value.sessionID, this.user$.value.uid);
    } catch (error) {
      this.rollbar.error('Failed to Join Dolby Session', error, locals);
      this.toastrService.error(
        `This may be due to a network issue.  Check your connection, refresh the page, and try again.<br/><br/><code>${error.message}</code>`,
        `Failed to join the conversation`,
        {
          enableHtml: true,
          progressBar: false,
          closeButton: true,
          tapToDismiss: false,
          toastComponent: GeneralToastComponent,
          disableTimeOut: true,
        }
      );
      await this.analyticsService.track('joined recording session on the stage', { success: false });
    } finally {
      this.joining = false;
    }
  }

  async checkAvailability() {
    const availability = await this.dolbyService.checkSeatCountAvailability();
    if (!availability.stageSeatAvailable) {
      this.joining = false;
      this.toastrService.info(
        `${this.walletService.studioPlan$.value.inStudio} is the maximum for your ${this.walletService.studioPlan$.value.name} plan.` +
          `${
            this.walletService.studioPlan$.value.name.includes('studio')
              ? ''
              : ' If you need more seats please consider upgrading your plan!'
          }`,
        `${availability.stageParticipantCount} Participants are already on The Stage`,
        {
          progressBar: false,
          closeButton: true,
          tapToDismiss: false,
          disableTimeOut: true,
          toastComponent: GeneralToastComponent,
        }
      );
      return false;
    }
    return true;
  }

  async openSupportCenter() {
    this.supportCenterService.show();
    await this.analyticsService.track('opened support center');
  }

  setupActiveParticipants() {
    // we only use this for dolby sessions to show who else is already in the sesh
    if (this.session$.value?.confType !== 'daily') {
      const sub = this.dolbyService.activeParticipants$.subscribe((data) => {
        this.activeParticipants = data?.participants.map((p) => p.info.externalId);
      });
      this.subs.push(sub);
    }
  }

  setName() {
    const combinedName = this.pronouns ? this.name + ` (${this.pronouns})` : this.name;
    if (combinedName) this.nameService.setName(this.session$.value.sessionID, this.user$.value.uid, combinedName);
  }

  setupName() {
    const sub = this.nameService
      .getName(this.session$.value.sessionID, this.user$.value.uid)
      .subscribe((name: string) => {
        if (!name) {
          return;
        }
        //const nameRegex = /^.+(?=\s\(.+(?=\)$))/; \\better, but incompatible with safari
        const nameRegex2 = /^.+\(/;
        const nameMatch = name.match(nameRegex2);
        //const pronounRegex = /(?<=\b\s\().+(?=\)$)/; \\better, but incompatible with safari
        const pronounRegex2 = /\(((\w|\d)+\/?(\w|\d)+)\)$/;
        this.name = nameMatch ? nameMatch[0].slice(0, nameMatch[0].length - 2) : name;
        const pronounsMatch = name.match(pronounRegex2);
        this.pronouns = pronounsMatch ? pronounsMatch[0].slice(1, pronounsMatch[0].length - 1) : '';
      });

    this.subs.push(sub);
  }

  setupWallet() {
    if (this.participant$.value?.role >= this.roleLimits.ONE_TIME_CHARGE_CREATE) {
      const sub = this.walletService.studioWallet$.subscribe(async (wallet) => {
        if (!wallet) return;
        const secondsAvailable = this.walletService.convertTimeCodeToSeconds(wallet.available);
        const oneHourInSeconds = 3600 * 1;
        await this.trialService.trialStatus$.nextExistingValue();
        await this.plan$.nextExistingValue();
        const unlimited = this.plan$.value?.recordingHours === 'Unlimited'; // recordingHours = "Unlimited"

        if (
          secondsAvailable <= oneHourInSeconds &&
          this.trialService.trialStatus$.value.onTrial !== true &&
          !this.timeWalletToast &&
          unlimited === false
        ) {
          this.available = wallet.available;
          this.isBelowOneHour = true;

          const toast: ActiveToast<GeneralToastComponent> = this.toastrService.info(
            null,
            `You have ${this.available} Recording Time remaining for this month`,
            {
              progressBar: false,
              closeButton: true,
              tapToDismiss: false,
              disableTimeOut: true,
              toastComponent: GeneralToastComponent,
            }
          );

          if (this.studioOrg$.value.driveID) {
            toast.toastRef.componentInstance.buttons = [
              {
                label: 'View Descript Subscription',
                handler: () => {
                  window.open(
                    `${environment.descript.webDomain}/view/settings/account?active=subscription&drive_id=${this.studioOrg$.value.driveID}&utm_source=squadcast`,
                    '_blank'
                  );
                },
              },
            ];
          } else {
            toast.toastRef.componentInstance.buttons = [
              {
                label: 'Add 1 hour for $5',
                handler: this.addOneHour.bind(this),
              },
            ];
          }

          await this.analyticsService.track('opened low recording time warning');

          this.timeWalletToast = toast;
        } else {
          this.isBelowOneHour = false;

          if (this.timeWalletToast) {
            this.toastrService.remove(this.timeWalletToast.toastId);
          }
        }
      });

      this.subs.push(sub);
    }
  }

  async addOneHour() {
    this.toastrService.remove(this.timeWalletToast.toastId);
    let charge: any = null;
    const amount = 500;
    const descriptionAmount = '$5.00';

    charge = await this.amplionService
      .chargeCustomer({
        amount,
        customerID: this.walletService.customerID$.value,
        description: `One-time purchase of 1 recording hour for ${descriptionAmount}`,
        metadata: { hours: 1 },
      })
      .catch(async (err) => {
        const toast: ActiveToast<GeneralToastComponent> = this.toastrService.error(
          `You may want to update your Payment Info`,
          `Failed to purchase 1 Recording Hour`,
          {
            progressBar: false,
            closeButton: true,
            tapToDismiss: false,
            disableTimeOut: true,
            toastComponent: GeneralToastComponent,
          }
        );

        toast.toastRef.componentInstance.buttons = [
          {
            label: 'Update Payment Info',
            handler: this.openUpdatePaymentInfoModal.bind(this),
          },
        ];

        await this.analyticsService.track('purchased recording time', { success: false, descriptionAmount, amount });
      });

    if (charge) {
      this.toastrService.success('Thank You!', `Successfully purchased 1 Recording Hour`, {
        progressBar: true,
        progressAnimation: 'decreasing',
        closeButton: true,
        tapToDismiss: false,
        timeOut: 10 * 1000,
        toastComponent: GeneralToastComponent,
      });

      await this.analyticsService.track('purchased recording time', { success: true, descriptionAmount, amount });
    }
  }

  async openUpdatePaymentInfoModal() {
    const modal = await this.modalController.create({
      component: UpdatePaymentInfoPage, //PaymentInfoCardComponent
      componentProps: {},
      showBackdrop: true,
      backdropDismiss: true,
      animated: true,
      cssClass: 'update-payment-info-modal',
    });
    await modal.present();
    await modal.onDidDismiss();
    const toast: ActiveToast<GeneralToastComponent> = this.toastrService.info(
      null,
      `You have ${this.available} Recording Time remaining for this month`,
      {
        progressBar: false,
        closeButton: true,
        tapToDismiss: false,
        disableTimeOut: true,
        toastComponent: GeneralToastComponent,
      }
    );

    toast.toastRef.componentInstance.buttons = [
      {
        label: 'Add 1 hour for $5',
        handler: this.addOneHour.bind(this),
      },
    ];
    await this.analyticsService.track('opened edit payment info');
  }

  async toggleEasterEgg() {
    this.easterEgg = !this.easterEgg;

    if (this.easterEgg) {
      const toast: ActiveToast<GeneralToastComponent> = this.toastrService.success(
        `Where the Green Room is green and everything else is normal. Take a picture and share it with us on social @squadcastfm #SquadShot`,
        `Welcome to the Green Green Room easter egg`,
        {
          progressBar: true,
          progressAnimation: 'decreasing',
          closeButton: true,
          tapToDismiss: false,
          timeOut: 30 * 1000,
          toastComponent: GeneralToastComponent,
        }
      );

      toast.toastRef.componentInstance.buttons = [
        {
          label: `Change It Back`,
          handler: () => {
            this.toggleEasterEgg();
          },
        },
      ];
    }

    await this.analyticsService.track('toggled green room easter egg', { enabled: this.easterEgg });
  }

  async setupUser() {
    await this.userService.activeUser$.nextExistingValue();
    if (this.user$.value.displayName) this.name = this.user$.value.displayName;
    if (this.user$.value.pronouns) this.pronouns = this.user$.value.pronouns;
    this.setupName();
    await this.trialService.trialStatus$.nextExistingValue();

    this.analyticsService.track('entered green room', {
      email: this.user$.value.email,
      id: this.session$.value.sessionID,
      castID: this.user$.value.uid,
      when: new Date().toLocaleString(),
      title: this.session$.value.sessionTitle,
      // planID: this.plan.id,
      trialStatus: this.trialService.trialStatus$.value.onTrial ? this.trialService.trialStatus$.value.daysLeft : 0,
    });
  }

  async setupPlatformCheck() {
    this.deviceInfo = this.deviceDetectorService.getDeviceInfo();
    let os = this.deviceInfo.os;
    const browser = this.deviceInfo.browser;
    const version = parseFloat(this.deviceInfo.browser_version);
    const device = this.deviceInfo.device;
    const iPad = this.iPadService.check();

    if (os === 'Mac' && this.iPadService.check()) {
      os = 'iOS';
    }

    if (
      (browser === 'Chrome' && version < 49) || // Brave & Silk Browsers read as Chrome
      (browser === 'Firefox' && version < 88 && os !== 'iOS') ||
      (browser === 'Firefox' && version < 40 && os === 'iOS') ||
      (browser === 'MS-Edge-Chromium' && version < 79) ||
      (browser === 'Opera' && version < 36) || // https://en.wikipedia.org/wiki/History_of_the_Opera_web_browser#Opera_2013
      browser === 'MS-Edge' ||
      browser === 'IE'
    ) {
      this.notSupported = true;
      const toast: ActiveToast<GeneralToastComponent> = this.toastrService.info(
        `You may need to update your Web Browser`,
        `${browser} ${version} on ${os} does not support SquadCast`,
        {
          progressBar: false,
          closeButton: false,
          tapToDismiss: false,
          toastComponent: GeneralToastComponent,
          disableTimeOut: true,
        }
      );

      toast.toastRef.componentInstance.learnMoreTopic = 'systemRequirements';
      await this.analyticsService.track('opened update web browser warning');
    }

    // ChromeOS (ChromeBooks)
    if (os === 'Chrome OS' || os === 'Chrome OS Cogent' || os === 'CrOS') {
      this.notSupported = true;
      const toast: ActiveToast<GeneralToastComponent> = this.toastrService.error(
        `Chrome OS is not supported. Please use a different device to join your session.`,
        `${os} does not support SquadCast`,
        {
          progressBar: false,
          closeButton: true,
          tapToDismiss: false,
          toastComponent: GeneralToastComponent,
          disableTimeOut: true,
        }
      );
      this.muteService.setMute(this.session$.value.sessionID, this.user$.value.uid, true);
      toast.toastRef.componentInstance.learnMoreTopic = 'systemRequirements';
      await this.analyticsService.track('opened chromebook os web browser error');
    }

    // iPhone & iPad Safari
    if (browser === 'Safari' && version >= 15.2 && (device === 'iPhone' || iPad)) {
      const toast: ActiveToast<GeneralToastComponent> = this.toastrService.info(
        `Mileage may vary. Take part in shaping SquadCast by test-driving early releases. Let us know what you think. If you want a fully stable recording, switch devices.`,
        `🚀 SquadCast for iPhone and iPad is in BETA 🎛️`,
        {
          progressBar: false,
          closeButton: false,
          tapToDismiss: false,
          disableTimeOut: true,
          toastComponent: GeneralToastComponent,
        }
      );

      toast.toastRef.componentInstance.buttons = [
        {
          label: 'Got It',
          handler: () => {
            toast.toastRef.close();
          },
        },
      ];
    }
  }

  /**
   * Set the conference type based on the feature flag.
   * If daily feature flag is disabled then do not allow the session to be a Daily Type.
   */
  async setupConfType() {
    const sub = this.featureFlagsService.featureFlags$.subscribe(async (flags) => {
      const confFeature = flags.find((flag) => flag.id === FeatureNames.DAILY);
      if (!confFeature.enabled && this.session$.value.confType === ConfTypes.DAILY) {
        await this.sessionsService.setSessionConfType(this.session$.value.sessionID, ConfTypes.CLASSIC);
        // Force a full page reload
        window.location.reload();
      } else if (
        confFeature.enabled &&
        (this.session$.value.confType === ConfTypes.CLASSIC ||
          !this.session$.value.confType ||
          this.session$.value.confType === ConfTypes.VOICE)
      ) {
        await this.sessionsService.setSessionConfType(this.session$.value.sessionID, ConfTypes.DAILY);
        // Force a full page reload
        window.location.reload();
      }
    });

    this.subs.push(sub);
  }

  navigateToDashboard() {
    this.router.navigate(['/dashboard'], {
      replaceUrl: true,
    });
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => {
      sub.unsubscribe();
    });
  }
}
